import { useTranslation } from 'react-i18next'
import { toLocale, type Locale } from './i18n.ts'

/**
 * Locale-aware date/time formatting on the platform `Intl` APIs (EPT-127).
 * Every helper takes the *resolved* UI locale — from the request middleware on
 * the server, from `i18n.language` in components — never the browser runtime,
 * so server render and client hydration always format identically. For the same
 * reason, callers rendering calendar dates or clock times pass the client-hint
 * time zone (`useHints().timeZone`) rather than letting `Intl` fall back to the
 * runtime zone, which differs between server and browser. Callers keep
 * `<time dateTime>` attributes ISO regardless of locale.
 */

/**
 * The resolved UI locale for components — `i18n.language` narrowed to the
 * supported-locale registry. On the server i18next carries the middleware's
 * detection; on the client it reads `<html lang>` (set by that same detection),
 * so both sides hand these helpers the identical locale.
 */
export function useLocale(): Locale {
	const { i18n } = useTranslation()
	return toLocale(i18n.language)
}

/**
 * `Intl.*Format` construction dominates the cost of a single format call, and
 * the admin tables format once per row — so formatters are cached per key
 * (locale, plus time zone where it applies). The supported-locale registry is
 * tiny, so each cache tops out at a handful of entries.
 */
function memoize<Formatter>(create: (key: string) => Formatter) {
	const cache = new Map<string, Formatter>()
	return (key: string): Formatter => {
		let formatter = cache.get(key)
		if (!formatter) {
			formatter = create(key)
			cache.set(key, formatter)
		}
		return formatter
	}
}

/** Largest-first unit thresholds for {@link formatRelativeTime}. */
const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
	['year', 1000 * 60 * 60 * 24 * 365],
	['month', 1000 * 60 * 60 * 24 * 30],
	['week', 1000 * 60 * 60 * 24 * 7],
	['day', 1000 * 60 * 60 * 24],
	['hour', 1000 * 60 * 60],
	['minute', 1000 * 60],
	['second', 1000],
]

const relativeFormatter = memoize(
	(locale) => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
)

/**
 * "vor 3 Tagen" / "3 days ago" — the largest fitting unit, with `numeric:
 * 'auto'` phrasing ("gestern"/"yesterday"). `now` comes from the caller (one
 * per render) so every stamp shares a reference point across SSR + hydration.
 */
export function formatRelativeTime(
	locale: Locale,
	value: Date | string,
	now: Date,
): string {
	const formatter = relativeFormatter(locale)
	const diff = new Date(value).getTime() - now.getTime()
	for (const [unit, ms] of RELATIVE_UNITS) {
		if (Math.abs(diff) >= ms) {
			return formatter.format(Math.round(diff / ms), unit)
		}
	}
	return formatter.format(0, 'second')
}

const dateTimeFormatter = memoize((key) => {
	const [locale, timeZone] = key.split('|')
	return new Intl.DateTimeFormat(locale, {
		dateStyle: 'medium',
		timeStyle: 'short',
		timeZone: timeZone || undefined,
	})
})

/** "05.03.2024, 12:00" / "Mar 5, 2024, 12:00 PM" — the absolute timestamp style. */
export function formatDateTime(
	locale: Locale,
	value: Date | string,
	options?: { timeZone?: string },
): string {
	return dateTimeFormatter(`${locale}|${options?.timeZone ?? ''}`).format(
		new Date(value),
	)
}

const dateFormatter = memoize((key) => {
	const [locale, timeZone] = key.split('|')
	return new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		timeZone: timeZone || undefined,
	})
})

/** "5. März 2024" / "Mar 5, 2024" — the date style shared by blog + admin lists. */
export function formatDate(
	locale: Locale,
	value: Date | string,
	options?: { timeZone?: string },
): string {
	return dateFormatter(`${locale}|${options?.timeZone ?? ''}`).format(
		new Date(value),
	)
}
